/* global process */

import axiosInstance from "../security/interceptor";

const resolveAnalyticsAgentServiceBaseUrl = () => {
  const configuredBaseUrl = String(
    process.env.REACT_APP_ANALYTICS_AGENT_BASE_URL ||
      process.env.VITE_ANALYTICS_AGENT_BASE_URL ||
      process.env.ANALYTICS_AGENT_BASE_URL ||
      "",
  ).trim();

  return configuredBaseUrl;
};

const LEGACY_ANALYTICS_AGENT_ENDPOINT_PATH = "/agents/vhub_analytics";

const isNotFoundError = (error) => Number(error?.response?.status) === 404;

const trimTrailingSlashes = (value = "") => {
  let endIndex = value.length;
  while (endIndex > 0 && value[endIndex - 1] === "/") {
    endIndex -= 1;
  }
  return value.slice(0, endIndex);
};

const trimLeadingSlashes = (value = "") => {
  let startIndex = 0;
  while (startIndex < value.length && value[startIndex] === "/") {
    startIndex += 1;
  }
  return value.slice(startIndex);
};

const buildAnalyticsAgentServiceUrl = (endpointPath) => {
  /* v8 ignore next -- exported analytics entrypoints always pass a defined endpoint path */
  const safeEndpointPath = String(endpointPath || "").trim();

  /* v8 ignore next 3 -- exported analytics entrypoints always pass a fixed endpoint path */
  if (!safeEndpointPath) {
    throw new Error("Analytics agent endpoint path is required.");
  }

  const baseUrl = resolveAnalyticsAgentServiceBaseUrl();

  /* v8 ignore next 3 -- direct agent requests only run when a dedicated agent host is configured */
  if (!baseUrl) {
    return safeEndpointPath;
  }

  try {
    return new URL(safeEndpointPath, baseUrl).toString();
  } catch {
    const normalizedBaseUrl = trimTrailingSlashes(baseUrl);
    const normalizedEndpointPath = trimLeadingSlashes(safeEndpointPath);
    return `${normalizedBaseUrl}/${normalizedEndpointPath}`;
  }
};

const defaultStartAgentRequest = async ({ endpointPath, query, requestId, userId, signal } = {}) => {
  const response = await axiosInstance.request({
    method: "post",
    url: buildAnalyticsAgentServiceUrl(endpointPath),
    data: {
      query,
      requestId,
      userId,
    },
    signal,
  });

  const payload = response?.data;

  if (payload?.lambda_execution_error) {
    throw new Error(payload?.errorMessage || "Analytics agent request failed.");
  }

  if (payload?.errorMessage) {
    throw new Error(payload.errorMessage);
  }

  return payload?.status || payload?.message || "";
};

export const createAnalyticsGraphApi = ({ graphQLRequest, startAgentRequest = defaultStartAgentRequest } = {}) => {
  const startVhubAnalyticsAgentViaGraphQL = async ({ query, requestId, userId, signal } = {}) => {
    const mutation = `mutation StartAnalyticsSummaryAgent($input: StartQueryInput!) {
      startAnalyticsSummaryAgent(input: $input)
    }`;
    const data = await graphQLRequest(mutation, {
      signal,
      variables: {
        input: {
          query,
          requestId,
          user_id: userId,
        },
      },
    });

    // With Event (async) invocation AppSync returns null for the field immediately
    // while Lambda runs in the background. Fall back to requestId only when the
    // field is explicitly present but null (async invocation); return "" when the
    // field is absent entirely so the acknowledgementErrorLabel path still fires.
    return data && Object.prototype.hasOwnProperty.call(data, "startAnalyticsSummaryAgent")
      ? (data.startAnalyticsSummaryAgent ?? requestId ?? "")
      : "";
  };

  const startVhubAnalyticsRequest = async ({
    endpointPath,
    query,
    requestId,
    userId,
    signal,
    queryErrorLabel,
    requestIdErrorLabel,
    userIdErrorLabel,
    acknowledgementErrorLabel,
  } = {}) => {
    const safeQuery = String(query || "").trim();
    const safeRequestId = String(requestId || "").trim();
    const safeUserId = String(userId || "").trim();

    if (!safeQuery.trim()) {
      throw new Error(queryErrorLabel);
    }

    if (!safeRequestId.trim()) {
      throw new Error(requestIdErrorLabel);
    }

    if (!safeUserId.trim()) {
      throw new Error(userIdErrorLabel);
    }

    const shouldUseDirectAgentRequest =
      startAgentRequest !== defaultStartAgentRequest || Boolean(resolveAnalyticsAgentServiceBaseUrl());
    let acknowledgement = "";

    if (!shouldUseDirectAgentRequest) {
      acknowledgement = await startVhubAnalyticsAgentViaGraphQL({
        query: safeQuery,
        requestId: safeRequestId,
        userId: safeUserId,
        signal,
      });
    } else {
      try {
        acknowledgement = await startAgentRequest({
          endpointPath,
          query: safeQuery,
          requestId: safeRequestId,
          userId: safeUserId,
          signal,
        });
      } catch (error) {
        if (!isNotFoundError(error) || endpointPath === LEGACY_ANALYTICS_AGENT_ENDPOINT_PATH) {
          throw error;
        }

        acknowledgement = await startAgentRequest({
          endpointPath: LEGACY_ANALYTICS_AGENT_ENDPOINT_PATH,
          query: safeQuery,
          requestId: safeRequestId,
          userId: safeUserId,
          signal,
        });
      }
    }

    if (!acknowledgement) {
      throw new Error(acknowledgementErrorLabel);
    }

    return acknowledgement;
  };

  const startVhubAnalyticsAgent = async ({ query, requestId, userId, signal } = {}) => {
    return startVhubAnalyticsRequest({
      endpointPath: "/agents/vhub_analytics_summary",
      query,
      requestId,
      userId,
      signal,
      queryErrorLabel: "Analytics summary query is required.",
      requestIdErrorLabel: "Analytics summary request ID is required.",
      userIdErrorLabel: "Analytics summary user ID is required.",
      acknowledgementErrorLabel: "Analytics summary agent did not acknowledge the request.",
    });
  };

  const startVhubAnalyticsProcessFlowAgent = async ({ query, requestId, userId, signal } = {}) => {
    return startVhubAnalyticsRequest({
      endpointPath: "/agents/vhub_analytics_process_flow",
      query,
      requestId,
      userId,
      signal,
      queryErrorLabel: "Analytics process flow query is required.",
      requestIdErrorLabel: "Analytics process flow request ID is required.",
      userIdErrorLabel: "Analytics process flow user ID is required.",
      acknowledgementErrorLabel: "Analytics process flow agent did not acknowledge the request.",
    });
  };

  const fetchAnalyticsArcData = async ({ filter = {}, signal } = {}) => {
    const query = `query GetAnalytics($filter: AnalyticsFilterInput) {
      getAnalytics(filter: $filter) {
        total_connections
        last_refreshed_at
        date_range
        items {
          distributorname
          confidence_bucket
          connection_share
          connections
          distributor_group
          distributorcode
          from_status
          to_status
          vehicle_count
          mean_time_days
          median_time_days
          plantdescription
          total_time_days
          total_connections_per_distributor_plant
          production_origin_group
          plantshortdescription
          p95_time_days
          salesseriesname
        }
      }
    }`;
    const data = await graphQLRequest(query, {
      signal,
      variables: { filter },
    });
    const payload = data?.getAnalytics;

    if (!payload) {
      throw new Error("Analytics arc API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsGraphData = async ({ filter = {}, signal } = {}) => {
    const query = `query GetAnalyticsGraph($filter: AnalyticsFilterInput) {
      getAnalyticsGraph(filter: $filter) {
        nodes {
          nodeName
          properties
        }
        edges {
          avg_time_days
          confidence_bucket
          confidence_score
          median_time_days
          p95_time_days
          total_connections
          vehicle_count
          sourceNode
          targetNode
        }
      }
    }`;
    const data = await graphQLRequest(query, {
      signal,
      variables: { filter },
    });
    const payload = data?.getAnalyticsGraph;

    if (!payload) {
      throw new Error("Analytics graph API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsStatusVehicles = async ({ input = {}, signal } = {}) => {
    const query = `query GetAnalyticsStatusVehicles($input: AnalyticsStatusVehiclesInput!) {
      getAnalyticsStatusVehicles(input: $input) {
        limit
        hasMore
        dateRangeStart
        dateRangeEnd
        items {
          vehiclesk
          vin
          productionvin
          preproductionvin
          status
          substatus
          previousStatus
          previousSubstatus
          nextStatus
          nextSubstatus
          statusStartTimestamp
          statusEndTimestamp
        }
      }
    }`;
    const data = await graphQLRequest(query, {
      signal,
      variables: { input },
    });
    const payload = data?.getAnalyticsStatusVehicles;

    if (!payload) {
      throw new Error("Analytics status vehicle API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsCardsData = async ({ input, signal } = {}) => {
    const query = `query GetAnalyticsCardsData($input: SelectedFiltersInput!) {
      getAnalyticsCardsData(input: $input) {
        plantshortdescription
        result {
          connections
          median_time_days
          slowest_path
          fastest_path
          total_rows
          num_vehicle_series
          num_distributors
          num_plants
          num_vehicles
          num_l1_statuss
          num_l2_status
        }
      }
    }`;
    const data = await graphQLRequest(query, {
      signal,
      variables: { input },
    });
    const payload = data?.getAnalyticsCardsData;

    if (!payload) {
      throw new Error("Analytics cards API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsOverview = async ({ signal } = {}) => {
    const query = `query GetAnalyticsOverview {
      getAnalyticsOverview {
        total_vehicle_count
        total_connections
        num_vehicle_series
        num_distributors
        num_plants
        num_l1_status
        num_l2_status
        last_refreshed_at
        date_range
      }
    }`;

    const data = await graphQLRequest(query, { signal });
    const payload = data?.getAnalyticsOverview;

    if (!payload) {
      throw new Error("Analytics overview API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsCommonPathData = async ({ filter, signal } = {}) => {
    const query = `query GetCommonPath($filter: CommonPathFilterInput!) {
      getCommonPath(filter: $filter) {
        total_vehicle_count
        items {
          transition_label
          step_number
          share_pct
          connections
          confidence_bucket
          avg_days
          median_time_days
          p95_time_days
        }
      }
    }`;
    const data = await graphQLRequest(query, {
      signal,
      variables: { filter },
    });
    const payload = data?.getCommonPath;

    if (!payload) {
      throw new Error("Common path API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsFilters = async ({ signal } = {}) => {
    const query = `query MyQuery {
      getAnalyticsFilters {
        combinations {
          production_origin_group
          plantshortdescription
          plantdescription
          distributorcode
          distributorname
          is_usa_distributor
          fleet_indicator
          salesseriesname
        }
        items {
          label
          value
          production_origin_group
          series {
            label
            value
            salesseriesname
          }
          distributors {
            label
            value
            distributorcode
            distributorname
            is_usa_distributor
            fleet_indicators {
              label
              value
              fleet_indicator
            }
          }
          plants {
            label
            value
            plantshortdescription
            plantdescription
            series {
              label
              value
              salesseriesname
            }
            distributors {
              label
              value
              distributorcode
              distributorname
              is_usa_distributor
              fleet_indicators {
                label
                value
                fleet_indicator
              }
            }
          }
        }
      }
    }`;

    const data = await graphQLRequest(query, { signal });
    const payload = data?.getAnalyticsFilters;

    if (!payload) {
      throw new Error("Analytics filters API did not return a response.");
    }

    return payload;
  };

  const fetchAnalyticsLinearFlowData = async ({ signal } = {}) => {
    const query = `query GetLinearFlowStaticData {
      getLinearFlowStaticData {
        linearFlowLabels
        error
      }
    }`;

    const data = await graphQLRequest(query, { signal });
    const payload = data?.getLinearFlowStaticData;

    if (!payload) {
      throw new Error("Expected linear flow API did not return a response.");
    }

    if (payload.error) {
      throw new Error(payload.error);
    }

    return payload;
  };

  return {
    fetchAnalyticsFilters,
    fetchAnalyticsArcData,
    fetchAnalyticsGraphData,
    fetchAnalyticsStatusVehicles,
    fetchAnalyticsCardsData,
    fetchAnalyticsOverview,
    fetchAnalyticsCommonPathData,
    fetchAnalyticsLinearFlowData,
    startVhubAnalyticsAgent,
    startVhubAnalyticsProcessFlowAgent,
  };
};
